// DEV-362: "Upcoming Compliance Tasks" on the employer Compliance page used to
// be hardcoded 2025 rows ("Due: July 31, 2025 · 48 days remaining"). Built
// from the client's tracked compliance items instead.

type ComplianceItemLike = {
  id: number | string;
  title?: string | null;
  category?: string | null;
  status?: string | null;
  dueDate?: string | Date | null;
};

export interface UpcomingComplianceTask<T> {
  item: T;
  dueDate: Date;
  /** Whole calendar days from today; negative when overdue. */
  daysUntilDue: number;
  /** "Due today" / "N days remaining" / "Overdue by N days" — never "remaining" for a past date. */
  dueLabel: string;
}

const DAY_MS = 24 * 60 * 60 * 1000;

// Due dates are date-only in practice (stored as midnight); read the Y-M-D part
// as a local date so a UTC timestamp doesn't shift the day in US timezones.
function toLocalDate(value: string | Date): Date | null {
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : new Date(value.getFullYear(), value.getMonth(), value.getDate());
  }
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value));
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(value);
  return isNaN(d.getTime()) ? null : new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

export function dueLabel(daysUntilDue: number): string {
  if (daysUntilDue === 0) return "Due today";
  if (daysUntilDue > 0) return `${daysUntilDue} day${daysUntilDue === 1 ? "" : "s"} remaining`;
  const overdue = -daysUntilDue;
  return `Overdue by ${overdue} day${overdue === 1 ? "" : "s"}`;
}

/** Open (not completed) items with a due date, soonest first. */
export function getUpcomingComplianceTasks<T extends ComplianceItemLike>(
  items: readonly T[],
  today: Date = new Date(),
): UpcomingComplianceTask<T>[] {
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate());
  const tasks: UpcomingComplianceTask<T>[] = [];
  for (const item of items) {
    if (String(item.status || "").toLowerCase() === "completed" || !item.dueDate) continue;
    const due = toLocalDate(item.dueDate);
    if (!due) continue;
    const daysUntilDue = Math.round((due.getTime() - start.getTime()) / DAY_MS);
    tasks.push({ item, dueDate: due, daysUntilDue, dueLabel: dueLabel(daysUntilDue) });
  }
  return tasks.sort((a, b) => a.dueDate.getTime() - b.dueDate.getTime());
}
